import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Camera, 
  Package,
  Barcode,
  Trash2,
  Truck,
  ArrowRightLeft
} from "lucide-react";

const API = "/api";

export default function MagazaSevkPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [depolar, setDepolar] = useState([]);
  const [gidenDepo, setGidenDepo] = useState("");
  const [gelenDepo, setGelenDepo] = useState("");
  const [sevkYontemi, setSevkYontemi] = useState("NORMAL");
  const [barkodInput, setBarkodInput] = useState("");
  const [urunler, setUrunler] = useState([]);
  const barkodRef = useRef(null);

  const toplamAdet = urunler.reduce((sum, u) => sum + u.miktar, 0);
  const urunCesidi = urunler.length;

  useEffect(() => {
    fetchDepolar();
  }, [token]);

  useEffect(() => {
    if (barkodRef.current) {
      barkodRef.current.focus();
    }
  }, [urunler]);

  const fetchDepolar = async () => {
    try {
      const response = await axios.get(`${API}/nebim/depolar?token=${token}`);
      if (response.data.success) {
        setDepolar(response.data.depolar || []);
      }
    } catch (error) {
      console.error("Depolar alınamadı:", error);
    }
  };

  const handleBarkodEkle = async () => {
    if (!barkodInput.trim()) return;
    if (!gidenDepo || !gelenDepo) {
      toast.error("Lütfen depoları seçin");
      return;
    }
    if (gidenDepo === gelenDepo) {
      toast.error("Giden ve gelen depo aynı olamaz");
      return;
    }

    try {
      const response = await axios.get(`${API}/nebim/urun-by-barkod?token=${token}&barkod=${barkodInput}`);
      
      if (response.data.success && response.data.urun) {
        const urun = response.data.urun;
        
        const existingIndex = urunler.findIndex(u => u.barkod === barkodInput);
        if (existingIndex >= 0) {
          const updated = [...urunler];
          updated[existingIndex].miktar += 1;
          setUrunler(updated);
        } else {
          setUrunler([...urunler, {
            barkod: barkodInput,
            urunKodu: urun.ProductCode,
            urunAdi: urun.ProductName,
            renk: urun.ColorDescription || urun.ColorCode,
            beden: urun.ItemDim1Code || "-",
            miktar: 1
          }]);
        }
        
        setBarkodInput("");
        toast.success(`${urun.ProductName} eklendi`);
      } else {
        toast.error("Ürün bulunamadı");
      }
    } catch (error) {
      toast.error("Ürün aranırken hata oluştu");
    }
  };

  const handleMiktarDegistir = (index, delta) => {
    const updated = [...urunler];
    updated[index].miktar = Math.max(1, updated[index].miktar + delta);
    setUrunler(updated);
  };

  const handleUrunSil = (index) => {
    setUrunler(urunler.filter((_, i) => i !== index));
  };

  const handleTransfer = async () => {
    if (!gidenDepo || !gelenDepo) {
      toast.error("Lütfen depoları seçin");
      return;
    }
    if (urunler.length === 0) {
      toast.error("Lütfen ürün ekleyin");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/nebim/depo-transfer?token=${token}`, {
        giden_depo: gidenDepo,
        gelen_depo: gelenDepo,
        sevk_yontemi: sevkYontemi,
        urunler: urunler.map(u => ({
          barkod: u.barkod,
          miktar: u.miktar
        }))
      });

      if (response.data.success) {
        toast.success(`${toplamAdet} adet ürün transfer edildi`);
        setUrunler([]);
      } else {
        toast.error(response.data.error || "Transfer başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transfer hatası");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleBarkodEkle();
    }
  };

  const gidenDepoAd = depolar.find(d => d.kod === gidenDepo)?.ad || gidenDepo;
  const gelenDepoAd = depolar.find(d => d.kod === gelenDepo)?.ad || gelenDepo;

  return (
    <div className="space-y-6 fade-in" data-testid="magaza-sevk-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Diğer Mağaza Sevk</h1>
          <p className="text-slate-500 text-sm">Mağazalar arası stok transferi</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Depo Seçimi */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Depo Seçimi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Giden Depo (Kaynak)</Label>
                  <Select value={gidenDepo} onValueChange={setGidenDepo}>
                    <SelectTrigger data-testid="select-giden-depo">
                      <SelectValue placeholder="Depo Seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {depolar.map((depo) => (
                        <SelectItem key={depo.kod} value={depo.kod}>
                          {depo.ad} ({depo.kod})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Gelen Depo (Hedef)</Label>
                  <Select value={gelenDepo} onValueChange={setGelenDepo}>
                    <SelectTrigger data-testid="select-gelen-depo">
                      <SelectValue placeholder="Depo Seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {depolar.filter(d => d.kod !== gidenDepo).map((depo) => (
                        <SelectItem key={depo.kod} value={depo.kod}>
                          {depo.ad} ({depo.kod})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sevk Yöntemi</Label>
                <Select value={sevkYontemi} onValueChange={setSevkYontemi}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NORMAL">Normal Sevk</SelectItem>
                    <SelectItem value="ACIL">Acil Sevk</SelectItem>
                    <SelectItem value="PLANLANAN">Planlanan Sevk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Barkod Okutma */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Barcode className="w-5 h-5" />
                Barkod Okutma
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button className="w-full bg-purple-600 hover:bg-purple-700" size="lg">
                <Camera className="w-5 h-5 mr-2" />
                Kamera ile Okut
              </Button>
              
              <div className="flex gap-2">
                <Input
                  ref={barkodRef}
                  placeholder="veya manuel barkod girin"
                  value={barkodInput}
                  onChange={(e) => setBarkodInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  data-testid="barkod-input"
                />
                <Button onClick={handleBarkodEkle} className="bg-purple-600 hover:bg-purple-700">
                  Ekle
                </Button>
              </div>

              {urunler.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Ürün eklemek için barkod okutun</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {urunler.map((urun, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{urun.urunAdi}</p>
                        <p className="text-xs text-slate-500">
                          {urun.urunKodu} | {urun.renk} | {urun.beden}
                        </p>
                        <p className="text-xs text-slate-400">{urun.barkod}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleMiktarDegistir(idx, -1)}
                        >
                          -
                        </Button>
                        <span className="w-10 text-center font-medium">{urun.miktar}</span>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleMiktarDegistir(idx, 1)}
                        >
                          +
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleUrunSil(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sağ Taraf - Özet */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Transfer Özeti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Toplam Ürün Adedi</span>
                  <span className="text-3xl font-bold text-purple-600">{toplamAdet}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Ürün Çeşidi:</span>
                  <span>{urunCesidi}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Sevk Yöntemi:</span>
                  <span>{sevkYontemi}</span>
                </div>
                
                {gidenDepo && gelenDepo && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <span className="font-medium">{gidenDepoAd}</span>
                      <ArrowRightLeft className="w-4 h-4 text-purple-500" />
                      <span className="font-medium">{gelenDepoAd}</span>
                    </div>
                  </div>
                )}
              </div>

              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700" 
                size="lg"
                disabled={loading || urunler.length === 0 || !gidenDepo || !gelenDepo}
                onClick={handleTransfer}
                data-testid="btn-transfer"
              >
                <Truck className="w-4 h-4 mr-2" />
                {loading ? "İşleniyor..." : "Transfer Yap"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
